import React, { useState, useEffect } from 'react';
import {
  Route,
  Routes,
  Link as ReactRouterLink,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import {
  Link,
  Box,
  Flex,
  Spacer,
  Stack,
  Image,
  Container,
  Select,
} from '@chakra-ui/react';
import { CloseIcon, HamburgerIcon, ExternalLinkIcon } from '@chakra-ui/icons';

import { Home, TransferDetails } from './screens';
import {
  Button,
  Input,
  MetaMaskIcon,
  Text,
  VersionInfo,
} from './components';
import { Wallet } from './lib/Wallet';
import styles from './theme/styles';

// blank is ok
export const defaultAddress = process.env.REACT_APP_DEFAULT_ADDRESS || '';

// Currently no navigation targets so disable menu for now - keep code
const menuEnabled = false;

const MenuToggle = ({ toggle, isOpen, width = '34px', height = '24px', ...props }) => {
  return (
    <Box display={{ base: 'block', md: 'none' }} onClick={toggle} {...props}>
      {isOpen ? <CloseIcon height={height} w={width} /> : <HamburgerIcon height={height} w={width} />}
    </Box>
  );
};

const MenuItem = ({ children, isLast, to = '/', ...rest }) => {
  return (
    <Link as={ReactRouterLink} to={to}>
      <Text textStyle="bodyGrey2" display="block" {...rest}>
        {children}
      </Text>
    </Link>
  );
};

const routes = [
  { path: '/transfer/:id', menuLink: false, label: 'Transfer', screen: TransferDetails },
];

const MenuLinks = ({ isOpen, ...props }) => {
  const menuLinks = routes.filter(({ menuLink }) => menuLink);
  return (
    <Box
      display={{ base: isOpen ? 'block' : 'none', md: 'block' }}
      flexBasis={{ base: '100%', md: 'auto' }}
      {...props}
    >
      <Stack
        spacing={8}
        align="center"
        justify={['center', 'space-between', 'flex-end', 'flex-end']}
        direction={['column', 'row', 'row', 'row']}
        pt={[4, 4, 0, 0]}
      >
        {/* <MenuItem to="/">Home</MenuItem> */}
        {menuLinks.map(({ label, path }, i) => (
          <MenuItem key={path} to={path} isLast={i === menuLinks.length - 1}>
            {label}
          </MenuItem>
        ))}
      </Stack>
    </Box>
  );
};

const NavBar = ({ children, ...props }) => {
  return (
    <Flex
      as="nav"
      align="center"
      justify={['center', 'space-between', 'flex-start', 'flex-start']}
      wrap="wrap"
      w="100%"
      mb={[6, 6, 8]}
      p={8}
      color="black"
      {...props}
    >
      {children}
    </Flex>
  );
};

const Footer = ({ children, style, ...props }) => {
  return (
    <Box
      d="flex"
      p={6}
      color="black"
      justifyContent="center"
      alignItems="flex-end"
      style={style}
      {...props}
    >
      {children}
    </Box>
  );
};

export default function App() {
  // check for address parameter
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(window.location.search);
  const queryAddress = queryParams.get('address');
  const [mode, setMode] = useState('metamask');
  const [address, setAddress] = useState(queryAddress || defaultAddress);
  const [isOpen, setIsOpen] = React.useState(false);
  const [connected, setConnected] = useState(false);
  const setAddressParam = (addr) => {
    const { history, document: { title }, location: { pathname } } = window;
    queryParams.set('address', addr);
    history.pushState({ address: addr }, `${title} (${addr})`, `${pathname}?${queryParams}`);
  };

  const changeAddress = (addr) => {
    const { pathname } = location;
    // If address is entered into input - navigate to home screen
    setAddress(addr);
    if (pathname !== '/') {
      navigate({
        pathname: '/',
        search: `?address=${addr}`,
      });
      // sync query param with address?
    } else if (addr !== queryAddress) {
      setAddressParam(addr);
    }
  };
  const wallet = new Wallet({ address });
  const connectWallet = async () => {
    await wallet.connect();
    setAddress(wallet.selectedAccount);
    setAddressParam(wallet.selectedAccount);
    setConnected(wallet.connected);
    setMode('metamask');
  };

  useEffect(() => {
    if (mode === 'metamask') {
      if (queryAddress === wallet.selectedAccount && queryAddress !== address) {
        setAddress(queryAddress);
      }
      if (queryAddress !== wallet.selectedAccount) {
        setMode('manual');
      }
    } else if (queryAddress !== address) {
      setAddress(queryAddress);
    }
  }, [queryAddress]);

  useEffect(() => {
    if (mode === 'metamask' && address !== wallet.selectedAccount) {
      setMode('manual');
    }
  }, [address]);
  const toggle = () => setIsOpen(!isOpen);
  return (
    <Box bgGradient="linear(white 0%, white 10%, grey5 130%)" minHeight="100vh" d="flex" flexDir="column">
      <Container maxW={['100%', '100%', '100%', '100%', '80%', '70%']} flex={1}>
        <NavBar px="0" py="4" d="flex" flexDirection="row" justifyContent="flex-start">
          <Box
            d="flex"
            width={['100%', 'auto']}
            justifyContent={['space-between', 'space-between', 'space-between', 'flex-start']}
            alignItems="center"
          >
            <Link
              as={ReactRouterLink}
              to={{
                pathname: '/',
                search: location.search,
              }}
              _active={{ outline: 'none', boxShadow: 'none' }}
              _focus={{ outline: 'none', boxShadow: 'none' }}
            >
              <Image mr="40px" height="30px" src="palm.svg" />
            </Link>
            {!menuEnabled ? null : (
              <>
                <MenuToggle toggle={toggle} isOpen={isOpen} />
                <MenuLinks isOpen={isOpen} />
              </>
            )}
          </Box>
          <Spacer />
          <Box
            d="flex"
            flexGrow={[1, 1, 0]}
            mt={['20px', 'auto']}
            flexDirection={['column', 'column', 'row']}
            justifyContent={['center', 'center', 'left']}
            alignItems={['left', 'left', 'center']}
          >
            <Input
              placeholder="Wallet Address"
              value={address}
              onChange={({ target: { value } }) => changeAddress(value)}
              style={styles.form.addressInput}
              minWidth={['100%', '100%', '360px']}
              textAlign="left"
              mr={[0, 0, '20px']}
              w="100%"
              borderRadius="0"
            />
            {mode === 'metamask' && !wallet.isMetamaskInstalled() ? (
              <Link isExternal href="https://metamask.io/">
                <Button
                  leftIcon={<ExternalLinkIcon color="white" width={8} />}
                  color="white"
                  w={['100%', '100%', 'auto']}
                  grow={1}
                  m={0}
                  pl="20px"
                  pr="30px"
                  mt={['10px', '10px', '0px']}
                  mr={[0, 0, '20px']}
                  height="40px"
                >
                  Get MetaMask
                </Button>
              </Link>
            ) : null}
            {mode === 'manual' || connected || !wallet.isMetamaskInstalled() ? null : (
              <Button
                textStyle="buttonLight"
                leftIcon={<MetaMaskIcon width={8} />}
                onClick={connectWallet}
                borderRadius="0"
                mr={[0, 0, '20px']}
                mt={['10px', '10px', '0px']}
                minWidth={['100%', '100%', '160px']}
                style={{ padding: '0 16px', h: '40px', fontWeight: 600 }}
              >
                Connect
              </Button>
            )}
            <Select
              value={mode}
              onChange={(e) => setMode(e.target.selectedOptions[0].value)}
              mr={[0, 0, '10px']}
              minWidth={['100%', '100%', '160px']}
              pr={0}
              textStyle="bodyGrey2"
              mt={['10px', '10px', '0px']}
              borderRadius="0"
            >
              <option style={styles.textStyles.bodyGrey2} value="manual">Manual</option>
              <option style={styles.textStyles.bodyGrey2} value="metamask">MetaMask</option>
            </Select>
          </Box>
        </NavBar>
        <Routes>
          <Route exact path="/" element={<Home address={address} />} />
          {routes.map(({ path, screen: Screen, hasProps = false }) => hasProps ? (
            <Route exact key={path} path={path} element={(props) => <Screen {...props} />} />
          ) : (
            <Route key={path} path={path} element={<Screen address={address} />} />
          ))}
        </Routes>
      </Container>
      <Footer>
        <VersionInfo />
      </Footer>
    </Box>
  );
}
